import { Leg, Stop, stopsAreSameStop } from "@vygruppen/vy-train-map";
import { WithInternalMessages } from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { StationEventResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/responseSchema";
import { useDropsRole } from "stores/useDropsRole";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

const uniqueLegs = (leg: Leg, index: number, allLegs: Leg[]) =>
  allLegs.findIndex(
    (otherLeg) =>
      stopsAreSameStop(leg.fromStop, otherLeg.fromStop) &&
      stopsAreSameStop(leg.toStop, otherLeg.toStop),
  ) === index;

type EventState = {
  eventMode: boolean;
};

type EventAction = {
  setEventMode: (newState: EventState["eventMode"]) => void;
};

export const useEventMode = createWithEqualityFn<EventState & EventAction>(
  (set) => ({
    eventMode: false,
    setEventMode: (newState) => set(() => ({ eventMode: newState })),
  }),
);

export const setEventMode = (newState: boolean) => {
  useEventMode.getState().setEventMode(newState);
};

export type StretchBuilderModalState =
  | {
      type: "infrastructure";
      stretchBuilderMode: boolean;
      eventInfo?: WithInternalMessages<InfrastructureResponse>;
    }
  | {
      type: "station";
      stretchBuilderMode: boolean;
      eventInfo?: WithInternalMessages<StationEventResponse>;
    }
  | {
      type: "closeOrEdit";
      uuids: { infrastructure: string[]; station: string[] };
    }
  | { type: "none" };

type State = {
  clickedStops: Stop[];
  selectedLegs: Leg[];
  selectedStops: Stop[];
  allStopsSelected: boolean;
  stretchBuilderModalState: StretchBuilderModalState;
};

type Action = {
  getStationEvent: () => WithInternalMessages<StationEventResponse> | undefined;
  getInfrastructureEvent: () =>
    | WithInternalMessages<InfrastructureResponse>
    | undefined;
  setClickedStops: (newState: State["clickedStops"]) => void;
  setSelectedLegs: (newState: State["selectedLegs"]) => void;
  setSelectedStops: (newState: State["selectedStops"]) => void;
  setAllStopsSelected: (newState: State["allStopsSelected"]) => void;
  setStretchBuilderModalState: (
    newState: State["stretchBuilderModalState"],
  ) => void;
  closeAndResetStretchBuilder: () => void;
  isStretchBuilderMode: () => boolean;
};

export const useStretchBuilder = createWithEqualityFn<State & Action>(
  (set, get) => ({
    clickedStops: [],
    selectedLegs: [],
    selectedStops: [],
    allStopsSelected: false,
    stretchBuilderModalState: { type: "none" },
    getStationEvent: () => {
      const { stretchBuilderModalState: state } = get();
      return state.type === "station" ? state.eventInfo : undefined;
    },
    getInfrastructureEvent: () => {
      const { stretchBuilderModalState: state } = get();
      return state.type === "infrastructure" ? state.eventInfo : undefined;
    },
    setClickedStops: (newState) => set(() => ({ clickedStops: newState })),
    setSelectedLegs: (newState) =>
      set(() => ({ selectedLegs: newState.filter(uniqueLegs) })),
    setSelectedStops: (newState) => set(() => ({ selectedStops: newState })),
    setAllStopsSelected: (newState) =>
      set(() => ({ allStopsSelected: newState })),
    setStretchBuilderModalState: (newState) => {
      set(() => ({
        stretchBuilderModalState: useDropsRole
          .getState()
          .isVaktlederOrDriftledningenOrTrafikkplanlegger()
          ? newState
          : { type: "none" },
      }));
    },
    closeAndResetStretchBuilder: () => {
      useEventMode.getState().setEventMode(false);
      set(() => ({
        stretchBuilderModalState: { type: "none" },
        clickedStops: [],
        selectedLegs: [],
        selectedStops: [],
        allStopsSelected: false,
      }));
    },
    isStretchBuilderMode: () => {
      const state = get().stretchBuilderModalState;
      return (
        (state.type === "infrastructure" || state.type === "station") &&
        state.stretchBuilderMode
      );
    },
  }),
  shallow,
);
